import useCustomDropzone from 'hooks/useCustomDropzone';
import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import {
  FilesUploadStore,
  FileUploadState,
  FileUploadStatus,
} from 'hooks/filesUploadStoreFactory';
import showToast from 'utils/toast';
import { ReactNode, useMemo } from 'react';
import useParallelScheduler from './useParallelScheduler';
import { fileUploaderMeta } from 'components/organisms/FileUploader/FileUploader.types';
import { groupBy, isEmpty, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ONE_MB } from 'utils/fileSizes';

export type OnUploadParams = {
  file: File;
  fileShortType: string;
  options?: any;
  onProgress: (progress: number | string) => void;
};

type OnUploadFn = (params: OnUploadParams) => Promise<any>;

type SchedulerFn = (job: () => Promise<unknown>) => void;

type NextFn = () => void;

type CustomDropzoneOptions = DropzoneOptions & {
  onAfterDropAccepted?: (next: NextFn) => void;
};

export type CustomDropzoneConfig = {
  options?:
    | CustomDropzoneOptions
    | ((meta: fileUploaderMeta) => CustomDropzoneOptions);
  onUpload: OnUploadFn;
  scheduler?: SchedulerFn;
  defaultSchedulerOptions?: {
    keepAlive?: boolean;
    onComplete?: (meta: fileUploaderMeta) => void;
  };
};

const tag = 'DROPZONE_STORE';
let uid = 0;

export type InitialFileUploadState = {
  file: File;
} & Partial<Pick<FileUploadState, 'key' | 'options'>>;

const getFileUploadState = (
  params: InitialFileUploadState,
): FileUploadState => {
  return {
    key: params.key ?? `${tag}_${uid++}`,
    file: params.file,
    progress: 0,
    status: FileUploadStatus.waiting,
    result: null,
    options: params.options,
  };
};

type GetProcessFileUploadConfig = {
  onUpload: OnUploadFn;
  scheduler: SchedulerFn;
  storeAPI: FilesUploadStore;
};

const getProcessFileUpload =
  (config: GetProcessFileUploadConfig) => (params: InitialFileUploadState) => {
    const { onUpload, scheduler, storeAPI } = config;
    const fileState = getFileUploadState(params);
    storeAPI.set(fileState.key, fileState);
    scheduler(() => {
      storeAPI.update(fileState.key, { status: FileUploadStatus.uploading });
      return onUpload({
        file: fileState.file,
        fileShortType: fileState.file.type.split('/').pop() as string,
        options: fileState.options,
        onProgress: (progress) =>
          storeAPI.update(fileState.key, {
            progress,
            status: FileUploadStatus.uploading,
          }),
      })
        .then((documentId) =>
          storeAPI.update(fileState.key, {
            status: FileUploadStatus.done,
            progress: 100,
            result: documentId,
          }),
        )
        .catch(() =>
          storeAPI.update(fileState.key, { status: FileUploadStatus.error }),
        );
    });
  };

const filesStateToMeta = (filesState: FileUploadState[]) => {
  return {
    ...groupBy(filesState, 'status'),
    isEmpty: isEmpty(filesState),
  };
};

const useCustomDropzoneStore = (
  config: CustomDropzoneConfig,
  storeAPI: FilesUploadStore,
) => {
  const { defaultSchedulerOptions, onUpload, scheduler, options } = config;

  const { t } = useTranslation();

  const meta = useMemo(
    () => filesStateToMeta(storeAPI.values),
    [storeAPI.values],
  );
  // should keep alive be default ?
  const defaultScheduler = useParallelScheduler({
    keepAlive: defaultSchedulerOptions?.keepAlive,
    onComplete: () =>
      defaultSchedulerOptions?.onComplete?.(
        filesStateToMeta(storeAPI.getValues()),
      ),
  });

  const processFileUpload = getProcessFileUpload({
    onUpload,
    scheduler: scheduler || defaultScheduler,
    storeAPI,
  });

  const { onAfterDropAccepted, ...paramsOptions } =
    (typeof options === 'function' ? options(meta) : options) || {};

  const dropzoneAPI = useCustomDropzone({
    onDropRejected: (fileRejections, event) => {
      const acceptedTypes = uniq(
        Object.values(paramsOptions.accept || {}).flat(),
      ).join(', ');

      if (!acceptedTypes || !paramsOptions.maxSize) return;

      showToast(
        {
          title: t('upload_file.type_size.common.error', {
            types: acceptedTypes,
            size: `${paramsOptions.maxSize / ONE_MB} MB`,
          }),
        },
        { type: 'error' },
      );
    },
    onDropAccepted: (files) => {
      const next = () => files.forEach((file) => processFileUpload({ file }));
      if (onAfterDropAccepted) return onAfterDropAccepted(next);
      next();
    },
    ...paramsOptions,
  });

  return {
    storeAPI,
    dropzoneAPI,
    processFileUpload,
  };
};

export default useCustomDropzoneStore;
